import { useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "./CartContext";
import { useStoreRegion } from "./StoreRegionContext";
import { apiClient } from "../lib/api";
import ShippingEstimator from "./ShippingEstimator";

export default function CartDrawer() {
  const { items, subtotal, open, setOpen, updateQty, removeItem } = useCart();
  const { region, symbol, current } = useStoreRegion();
  const [checkingOut, setCheckingOut] = useState(false);
  const [shipping, setShipping] = useState(null);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    if (!shipping) {
      toast.error("Shipping address required", {
        description: "Please enter your shipping address and select a shipping method before checkout.",
      });
      return;
    }
    setCheckingOut(true);
    try {
      const { data } = await apiClient.post("/checkout/session", {
        items: items.map((i) => ({
          product_id: i.id,
          quantity: i.quantity,
          fragrance: i.fragranceName || null,
          color: i.colorName || null,
          image: i.image_url || null,
        })),
        origin_url: window.location.origin,
        region,
        shipping: shipping
          ? {
              service_code: shipping.service_code,
              service_name: shipping.service_name,
              amount_cad: shipping.amount_cad,
            }
          : null,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (e) {
      toast.error("Checkout failed", {
        description: e?.response?.data?.detail || e?.message,
      });
      setCheckingOut(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 bg-black/40 z-50"
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 280 }}
            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white z-50 flex flex-col"
            data-testid="cart-drawer"
          >
            <header className="flex items-center justify-between p-6 border-b border-zinc-100">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500">
                  Your Bag
                </div>
                <div
                  className="text-xl font-black tracking-tight"
                  style={{ fontFamily: "Outfit" }}
                >
                  {items.length} item{items.length !== 1 ? "s" : ""}
                </div>
              </div>
              <button
                onClick={() => setOpen(false)}
                data-testid="close-cart-btn"
                className="w-9 h-9 hover:bg-zinc-100 flex items-center justify-center"
                aria-label="Close cart"
              >
                <X size={18} strokeWidth={1.5} />
              </button>
            </header>

            {items.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
                <ShoppingBag size={42} strokeWidth={1} className="text-zinc-300 mb-4" />
                <p className="text-zinc-500 text-sm mb-6">Your bag is empty.</p>
                <Link
                  to="/"
                  onClick={() => setOpen(false)}
                  className="text-xs uppercase tracking-[0.25em] font-bold hover:text-[#002FA7]"
                >
                  Browse collection →
                </Link>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                  {items.map((it) => (
                    <div
                      key={it.lineKey || it.id}
                      data-testid={`cart-item-${it.lineKey || it.id}`}
                      className="flex gap-4 pb-6 border-b border-zinc-100 last:border-none"
                    >
                      <img
                        src={it.image_url}
                        alt={it.name}
                        className="w-20 h-20 object-cover bg-zinc-100 shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-sm leading-tight mb-1 truncate" style={{ fontFamily: "Outfit" }}>
                          {it.name}
                        </h3>
                        {it.colorName && (
                          <div
                            className="text-[10px] uppercase tracking-[0.2em] font-semibold text-zinc-700 mb-1 truncate"
                            data-testid={`cart-color-${it.lineKey}`}
                          >
                            Colour · {it.colorName}
                          </div>
                        )}
                        {it.fragranceName && (
                          <div
                            className="text-[10px] uppercase tracking-[0.2em] font-semibold luxe-text mb-1 truncate"
                            data-testid={`cart-fragrance-${it.lineKey}`}
                          >
                            {it.fragranceName === "Unscented" ? "Unscented" : `Scent · ${it.fragranceName}`}
                          </div>
                        )}
                        <div className="text-xs text-zinc-500 mb-3 font-mono">
                          {symbol}{it.price_usd.toFixed(2)}
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center border border-zinc-200">
                            <button
                              onClick={() => updateQty(it.lineKey || it.id, it.quantity - 1)}
                              data-testid={`cart-dec-${it.lineKey || it.id}`}
                              className="w-8 h-8 hover:bg-zinc-100 flex items-center justify-center"
                              aria-label="Decrease"
                            >
                              <Minus size={12} strokeWidth={2} />
                            </button>
                            <span className="w-8 text-center font-mono text-xs font-bold">
                              {it.quantity}
                            </span>
                            <button
                              onClick={() => updateQty(it.lineKey || it.id, it.quantity + 1)}
                              data-testid={`cart-inc-${it.lineKey || it.id}`}
                              className="w-8 h-8 hover:bg-zinc-100 flex items-center justify-center"
                              aria-label="Increase"
                            >
                              <Plus size={12} strokeWidth={2} />
                            </button>
                          </div>
                          <button
                            onClick={() => removeItem(it.lineKey || it.id)}
                            data-testid={`cart-remove-${it.lineKey || it.id}`}
                            className="text-zinc-400 hover:text-red-500"
                            aria-label="Remove"
                          >
                            <Trash2 size={14} strokeWidth={1.5} />
                          </button>
                        </div>
                      </div>
                      <div className="text-sm font-mono font-bold shrink-0">
                        {symbol}{(it.price_usd * it.quantity).toFixed(2)}
                      </div>
                    </div>
                  ))}
                </div>

                <footer className="border-t border-zinc-100 p-6 space-y-4 bg-zinc-50">
                  <ShippingEstimator
                    items={items}
                    symbol={symbol}
                    selected={shipping}
                    onSelect={setShipping}
                  />

                  <div className="border-t border-zinc-200 pt-4 space-y-1.5">
                    <div className="flex justify-between items-baseline">
                      <span className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500">
                        Subtotal · {current.code}
                      </span>
                      <span className="text-lg font-black font-mono" data-testid="cart-subtotal">
                        {symbol}{subtotal.toFixed(2)}
                      </span>
                    </div>
                    {shipping && (
                      <div className="flex justify-between items-baseline" data-testid="cart-shipping-line">
                        <span className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-500">
                          Shipping · {shipping.service_name}
                        </span>
                        <span className="text-sm font-bold font-mono">
                          {symbol}{shipping.amount_cad.toFixed(2)}
                        </span>
                      </div>
                    )}
                    {shipping && (
                      <div className="flex justify-between items-baseline pt-1 border-t border-zinc-100">
                        <span className="text-[10px] uppercase tracking-[0.3em] font-semibold text-zinc-700">
                          Total
                        </span>
                        <span className="text-2xl font-black font-mono" data-testid="cart-total">
                          {symbol}{(subtotal + shipping.amount_cad).toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>

                  {!shipping && (
                    <p className="text-[10px] text-zinc-500 text-center" data-testid="checkout-requirement-note">
                      Enter your shipping address above and select a shipping method to continue · taxes calculated at checkout
                    </p>
                  )}
                  <button
                    onClick={handleCheckout}
                    disabled={checkingOut || !shipping}
                    data-testid="checkout-btn"
                    className="w-full bg-zinc-900 text-white h-12 text-xs uppercase tracking-[0.25em] font-bold hover:bg-black transition inline-flex items-center justify-center gap-2 disabled:bg-zinc-400 disabled:cursor-not-allowed"
                  >
                    {checkingOut ? "Redirecting..." : (shipping ? "Checkout" : "Enter shipping address")}
                    {!checkingOut && shipping && <ArrowRight size={14} strokeWidth={2} />}
                  </button>
                </footer>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
